import type { CanonicalProduct } from '../menu/product.ts';
import { modifierPriceKey, type BusinessPolicySnapshot } from '../policy/business-policy.ts';
import { quoteForCommit, type QuoteResult } from '../pricing/quote.ts';
import { quoteForExplicitComboCommit } from '../pricing/explicit-quote.ts';
import { applyValidatedBenefitsToQuote } from '../pricing/benefit-quote.ts';
import type { ValidatedPricingBenefit } from '../loyalty/benefit-authority.ts';
import type { ConfirmedComboMatchInput } from '../pricing/explicit-combo.ts';
import { buildOrderLineSnapshot, type OrderLineSnapshot, type ProductSelectionInput } from '../order/product-selection.ts';
import { createOrderIntake, type CustomerSubmission } from '../order/order-intake.ts';
import {
  createOrderFinalizer,
  type CanonicalOrderSnapshot,
  type PriceDifferenceDecision,
  type PriceDifferencePolicy,
} from '../order/order-finalization.ts';
import {
  canonicalOrderChannelForSource,
  type LineServiceMode,
  type OrderSource,
  type ServiceMode,
} from '../contracts/operating-context.ts';
import { createChannelIntake, type ChannelEnvelope, type ChannelSubmission } from '../channels/channel-intake.ts';
import type { createPaymentAuthority, PaymentSnapshot } from '../payment/payment-authority.ts';
import { buildOrderSettlementUnits } from '../payment/order-settlement-units.ts';
import {
  projectSmtFulfillment,
  type createFulfillmentAuthority,
  type FulfillmentSnapshot,
} from '../fulfillment/fulfillment-authority.ts';
import { summarizeRequiredFlow, type FulfillmentTask } from '../fulfillment/required-flow.ts';
import type { createPrintAuthority, PrintJobSnapshot, PrintTargetIntent } from '../printing/print-authority.ts';
import type { createStaffAuthority } from '../staff/staff-authority.ts';
import type { createDurabilityCoordinator, OutboxRecord } from '../durability/durability.ts';
import type { createOperationalFacts, OperationalFact } from '../facts/operational-facts.ts';
import type { createTableAuthority } from '../table/table-authority.ts';
import type { createAvailabilityAuthority } from '../availability/availability-authority.ts';
import type { createBusinessDayCoordinator } from '../business-day/business-day-coordinator.ts';
import { buildAcceptedOrderReportingPayload } from './accepted-order-reporting-payload.ts';
import { createSmtP01WorkspaceAuthority } from './smt-p01-workspace.ts';
import { createSmtP01ActiveWorkAuthority } from './smt-p01-active-work.ts';
import { createSmtP01WorkspaceDurability } from './smt-p01-workspace-durability.ts';
import type { ActiveWorkHandlerContext, ActiveWorkSurface } from './active-work-custody.ts';
import type { SmtP01ActiveWorkFactInput } from './smt-p01-workspace-contract.ts';
import { createSmtOrdersSurface } from './smt-orders.ts';
import { createSmtDineInSurface } from './smt-dine-in.ts';
import { createSmtAvailabilitySurface } from './smt-availability.ts';
import { createSmtBusinessDaySurface } from './smt-business-day.ts';
import { createSmtReportingSurface } from './smt-reporting.ts';
import { createSmtPrintingSurface } from './smt-printing.ts';

type PaymentAuthority = ReturnType<typeof createPaymentAuthority>;
type FulfillmentAuthority = ReturnType<typeof createFulfillmentAuthority>;
type PrintAuthority = ReturnType<typeof createPrintAuthority>;
type StaffAuthority = ReturnType<typeof createStaffAuthority>;
type DurabilityCoordinator = ReturnType<typeof createDurabilityCoordinator>;
type OperationalFacts = ReturnType<typeof createOperationalFacts>;
type TableAuthority = ReturnType<typeof createTableAuthority>;
type AvailabilityAuthority = ReturnType<typeof createAvailabilityAuthority>;
type BusinessDayCoordinator = ReturnType<typeof createBusinessDayCoordinator>;

export interface FulfillmentPolicyDecision {
  readonly requiresPaymentBeforeProduction: boolean;
  readonly requiredTasks: readonly FulfillmentTask[];
}

export interface BusinessRuntimeDependencies {
  readonly getActivePolicy: () => BusinessPolicySnapshot | null;
  readonly getProduct: (productId: string) => CanonicalProduct | null;
  readonly isStoreReady: () => boolean;
  readonly resolveFulfillmentPolicy: (order: CanonicalOrderSnapshot) => FulfillmentPolicyDecision;
  readonly payment: PaymentAuthority;
  readonly fulfillment: FulfillmentAuthority;
  readonly print: PrintAuthority;
  readonly staff?: StaffAuthority;
  readonly table?: TableAuthority;
  readonly availability?: AvailabilityAuthority;
  readonly businessDay?: BusinessDayCoordinator;
  readonly durability: DurabilityCoordinator;
  readonly facts: OperationalFacts;
  readonly getStoreId?: () => string | null;
  readonly getBusinessDate?: () => string | null;
}

export interface OperatorOrderCommand {
  readonly intakeId: string;
  readonly orderId: string;
  readonly idempotencyKey: string;
  readonly businessDate: string;
  readonly source: Extract<OrderSource, 'smt' | 'smm'>;
  readonly createdAt: string;
  readonly operatorId: string;
  readonly deviceId?: string;
  readonly customerId?: string;
  readonly serviceMode: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly lines: readonly ProductSelectionInput[];
  readonly unitSettlement?: boolean;
}

export type OperatorWorkspaceOrderCommand = Omit<OperatorOrderCommand, 'lines' | 'source'> & {
  readonly source: 'smt';
  readonly workspaceId: string;
  readonly expectedWorkspaceRevision: number;
};

export type ActiveOperatorWorkspaceOrderCommand = Omit<
  OperatorOrderCommand,
  'lines' | 'deviceId' | 'serviceMode' | 'lineServiceModes'
> & {
  readonly workspaceId: string;
  readonly expectedWorkspaceRevision: number;
  readonly expectedCustodyRevision: number;
  readonly surface: ActiveWorkSurface;
  readonly deviceId: string;
  readonly sessionRef?: string;
  readonly serviceMode?: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
};

export interface SmmChannelOrderCommand {
  readonly submission: ChannelSubmission;
  readonly intakeId: string;
  readonly orderId: string;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly deviceId?: string;
  readonly customerId?: string;
  readonly serviceMode: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly at: string;
  readonly unitSettlement?: boolean;
}

export interface AcceptChannelOrderCommand {
  readonly submissionId: string;
  readonly intakeId: string;
  readonly orderId: string;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly deviceId?: string;
  readonly customerId?: string;
  readonly serviceMode: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly at: string;
  readonly pricePolicy?: PriceDifferencePolicy;
  readonly priceDecision?: PriceDifferenceDecision;
  readonly unitSettlement?: boolean;
}

export interface RejectChannelSubmissionCommand {
  readonly submissionId: string;
  readonly runtimeIntakeId: string;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly reason: string;
  readonly at: string;
}

export interface RuntimeOrderResult {
  readonly order: CanonicalOrderSnapshot;
  readonly payment: PaymentSnapshot;
  readonly fulfillment: FulfillmentSnapshot;
  readonly channel?: ChannelEnvelope;
}

export interface RecordRuntimePaymentCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
}

export interface RecordRuntimeCashTenderCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
  readonly tenderedMinor: number;
  readonly payerId?: string;
}

export interface RuntimeCashTenderResult {
  readonly payment: PaymentSnapshot;
  readonly changeMinor: number;
}

export interface RuntimeMutationCommand {
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
}

export interface RecordRuntimeRefundCommand extends RuntimeMutationCommand {
  readonly businessDate: string;
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
  readonly reason: string;
}

export interface RecordRuntimeCorrectionCommand extends RuntimeMutationCommand {
  readonly businessDate: string;
  readonly direction: 'in' | 'out';
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
  readonly reason: string;
}

export interface AllocateRuntimePaymentUnitsCommand extends RuntimeMutationCommand {
  readonly payerId: string;
  readonly unitIds: readonly string[];
}

export interface QueueOrderPrintCommand {
  readonly intentId: string;
  readonly orderId: string;
  readonly documentKind: string;
  readonly templateVersion: string;
  readonly payloadVersion: string;
  readonly targets: readonly PrintTargetIntent[];
  readonly requestedBy: string;
  readonly at: string;
}

export interface RuntimeCatalogOptionProjection {
  readonly optionId: string;
  readonly name: string;
  readonly enabled: boolean;
  readonly priceDeltaMinor: number | null;
}

export interface RuntimeCatalogOptionGroupProjection {
  readonly optionGroupId: string;
  readonly name: string;
  readonly required: boolean;
  readonly min: number;
  readonly max: number;
  readonly options: readonly RuntimeCatalogOptionProjection[];
}

export interface RuntimeCatalogProductProjection {
  readonly productId: string;
  readonly customerName: string;
  readonly operationsName: string;
  readonly currency: string;
  readonly basePriceMinor: number | null;
  readonly visible: boolean;
  readonly availability: 'available' | 'blocked' | 'unbound';
  readonly sellable: boolean;
  readonly requiresConfiguration: boolean;
  readonly hasOptions: boolean;
  readonly optionGroups: readonly RuntimeCatalogOptionGroupProjection[];
}

export interface RuntimeDraftPreview {
  readonly lines: readonly OrderLineSnapshot[];
  readonly quote: QuoteResult | null;
}

export interface RuntimeChannelPreview {
  readonly envelope: ChannelEnvelope;
  readonly lines: readonly OrderLineSnapshot[];
  readonly quote: QuoteResult | null;
  readonly differenceMinor?: number;
  readonly errorCode?: string;
}

interface FinalizeRuntimeOrderCommand {
  readonly intakeId: string;
  readonly orderId: string;
  readonly idempotencyKey: string;
  readonly businessDate: string;
  readonly source: OrderSource;
  readonly createdAt: string;
  readonly operatorId: string;
  readonly deviceId?: string;
  readonly customerId?: string;
  readonly serviceMode: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly lines: readonly ProductSelectionInput[];
  readonly customerSubmission?: CustomerSubmission;
  readonly pricePolicy?: PriceDifferencePolicy;
  readonly priceDecision?: PriceDifferenceDecision;
  readonly unitSettlement?: boolean;
  readonly confirmedComboMatches?: readonly ConfirmedComboMatchInput[];
  readonly validatedBenefits?: readonly ValidatedPricingBenefit[];
}

const quoteError = (kind: 'blocked' | 'rejected' | 'unbound'): Error =>
  new Error(`RUNTIME_ORDER_${kind.toUpperCase()}`);

const errorCode = (error: unknown): string => error instanceof Error ? error.message : 'RUNTIME_PREVIEW_FAILED';

const activeHandlerFor = (command: ActiveOperatorWorkspaceOrderCommand): ActiveWorkHandlerContext => ({
  actorId: command.operatorId,
  surface: command.surface,
  deviceId: command.deviceId,
  at: command.createdAt,
  ...(command.sessionRef?.trim() ? { sessionRef: command.sessionRef.trim() } : {}),
});

const canonicalLineServiceModes = (
  value: readonly LineServiceMode[] | undefined,
): readonly LineServiceMode[] | undefined => value === undefined
  ? undefined
  : [...value]
      .map((entry) => ({ lineId: entry.lineId, serviceMode: entry.serviceMode }))
      .sort((left, right) => left.lineId.localeCompare(right.lineId));

const sameLineServiceModes = (
  left: readonly LineServiceMode[] | undefined,
  right: readonly LineServiceMode[] | undefined,
): boolean => JSON.stringify(canonicalLineServiceModes(left) ?? []) === JSON.stringify(canonicalLineServiceModes(right) ?? []);

export function createBusinessRuntime(deps: BusinessRuntimeDependencies) {
  const orderIntake = createOrderIntake();
  const orderFinalizer = createOrderFinalizer();
  const channelIntake = createChannelIntake();

  const activePolicy = (): BusinessPolicySnapshot => {
    const policy = deps.getActivePolicy();
    if (!policy) throw new Error('RUNTIME_ACTIVE_POLICY_REQUIRED');
    return policy;
  };

  const requireOrder = (orderId: string): CanonicalOrderSnapshot => {
    const order = orderFinalizer.get(orderId);
    if (!order) throw new Error('RUNTIME_ORDER_NOT_FOUND');
    return order;
  };

  const requireChannel = (submissionId: string): ChannelEnvelope => {
    const envelope = channelIntake.get(submissionId);
    if (!envelope) throw new Error('RUNTIME_CHANNEL_SUBMISSION_NOT_FOUND');
    return envelope;
  };

  const buildLines = (inputs: readonly ProductSelectionInput[]): readonly OrderLineSnapshot[] => inputs.map((input) => {
    const product = deps.getProduct(input.productId);
    if (!product) throw new Error(`RUNTIME_PRODUCT_NOT_FOUND:${input.productId}`);
    return buildOrderLineSnapshot(product, input);
  });

  const previewDraft = (inputs: readonly ProductSelectionInput[]): RuntimeDraftPreview => {
    if (inputs.length === 0) return { lines: [], quote: null };
    const lines = buildLines(inputs);
    return { lines, quote: quoteForCommit(lines, activePolicy()) };
  };

  const appendFact = (
    order: CanonicalOrderSnapshot,
    eventId: string,
    factType: string,
    authority: string,
    revision: string,
    at: string,
    payload: unknown,
  ): OperationalFact => deps.facts.append({
    eventId,
    aggregateType: 'order',
    aggregateId: order.orderId,
    factType,
    businessDate: order.businessDate,
    occurredAt: at,
    recordedAt: at,
    schemaVersion: 1,
    source: { authority, revision },
    payload,
  });

  const appendFactForBusinessDate = (
    order: CanonicalOrderSnapshot,
    businessDate: string,
    eventId: string,
    factType: string,
    authority: string,
    revision: string,
    at: string,
    payload: unknown,
  ): OperationalFact => deps.facts.append({
    eventId,
    aggregateType: 'order',
    aggregateId: order.orderId,
    factType,
    businessDate,
    occurredAt: at,
    recordedAt: at,
    schemaVersion: 1,
    source: { authority, revision },
    payload,
  });

  const appendActiveWorkFact = (input: SmtP01ActiveWorkFactInput): void => {
    deps.facts.append({
      eventId: `active-work:${input.workspaceId}:${input.factType}:${input.workspaceRevision}:${input.custodyRevision}:${input.handler.at}`,
      aggregateType: 'active_work',
      aggregateId: input.workspaceId,
      factType: input.factType,
      businessDate: input.businessDate,
      occurredAt: input.handler.at,
      recordedAt: input.handler.at,
      schemaVersion: 1,
      source: {
        authority: 'active-work-custody',
        revision: `${input.workspaceRevision}:${input.custodyRevision}`,
      },
      payload: {
        actorId: input.handler.actorId,
        surface: input.handler.surface,
        deviceId: input.handler.deviceId,
        ...(input.handler.sessionRef ? { sessionRef: input.handler.sessionRef } : {}),
        workspaceRevision: input.workspaceRevision,
        custodyRevision: input.custodyRevision,
        ...(input.orderId ? { orderId: input.orderId } : {}),
      },
    });
  };

  const finalizeRuntimeOrder = (command: FinalizeRuntimeOrderCommand): RuntimeOrderResult => {
    const policy = activePolicy();
    const channel = canonicalOrderChannelForSource(command.source);
    const lines = buildLines(command.lines);

    const intake = orderIntake.accept({
      intakeId: command.intakeId,
      businessDate: command.businessDate,
      source: command.source,
      channel,
      createdAt: command.createdAt,
      createdBy: { operatorId: command.operatorId, ...(command.deviceId ? { deviceId: command.deviceId } : {}) },
      customerSubmission: command.customerSubmission,
      serviceMode: command.serviceMode,
      lineServiceModes: command.lineServiceModes,
      lines,
    }, { storeReady: deps.isStoreReady(), reviewRequired: false });

    const baseQuote = command.confirmedComboMatches === undefined
      ? quoteForCommit(lines, policy)
      : quoteForExplicitComboCommit(lines, policy, command.confirmedComboMatches);
    const quote = applyValidatedBenefitsToQuote(baseQuote, command.validatedBenefits ?? []);
    if (quote.kind !== 'quoted') throw quoteError(quote.kind);

    const order = orderFinalizer.finalize(intake, quote.evidence, {
      orderId: command.orderId,
      idempotencyKey: command.idempotencyKey,
      createdAt: command.createdAt,
      customerId: command.customerId,
      pricePolicy: command.pricePolicy,
      priceDecision: command.priceDecision,
    });

    const payment = deps.payment.open({
      orderId: order.orderId,
      orderRevision: order.revision,
      currency: order.currency,
      totalMinor: order.totalMinor,
      ...(command.unitSettlement ? { settlementUnits: buildOrderSettlementUnits(order) } : {}),
    });

    const fulfillmentPolicy = deps.resolveFulfillmentPolicy(order);
    const fulfillment = deps.fulfillment.open({
      orderId: order.orderId,
      orderRevision: order.revision,
      requiresPaymentBeforeProduction: fulfillmentPolicy.requiresPaymentBeforeProduction,
      requiredTasks: fulfillmentPolicy.requiredTasks,
      lines: order.lines.map((line) => ({ lineId: line.lineId, productId: line.productId, quantity: line.quantity })),
    });

    appendFact(
      order,
      `order:${order.orderId}:accepted:r${order.revision}`,
      'order.accepted',
      'order',
      String(order.revision),
      command.createdAt,
      buildAcceptedOrderReportingPayload(order),
    );

    return { order, payment, fulfillment };
  };

  const acceptChannelOrder = (
    command: AcceptChannelOrderCommand,
    validatedBenefits: readonly ValidatedPricingBenefit[] = [],
  ): RuntimeOrderResult => {
    const envelope = requireChannel(command.submissionId);
    if (envelope.status === 'rejected') throw new Error('RUNTIME_CHANNEL_ALREADY_REJECTED');
    if (envelope.status === 'accepted' && envelope.orderId && envelope.orderId !== command.orderId) {
      throw new Error('RUNTIME_CHANNEL_ALREADY_ACCEPTED');
    }

    let customerSubmission: CustomerSubmission | undefined;
    if (envelope.payload.customerDisplayedTotalMinor !== undefined) {
      if (!envelope.payload.currency) throw new Error('RUNTIME_CHANNEL_DISPLAYED_TOTAL_CURRENCY_REQUIRED');
      customerSubmission = {
        displayedTotalMinor: envelope.payload.customerDisplayedTotalMinor,
        currency: envelope.payload.currency,
        paymentReference: envelope.payload.paymentEvidenceRef,
      };
    }

    const result = finalizeRuntimeOrder({
      intakeId: command.intakeId,
      orderId: command.orderId,
      idempotencyKey: command.idempotencyKey,
      businessDate: envelope.businessDate,
      source: envelope.source,
      createdAt: envelope.createdAt,
      operatorId: command.operatorId,
      deviceId: command.deviceId,
      customerId: command.customerId,
      serviceMode: command.serviceMode,
      lineServiceModes: command.lineServiceModes,
      lines: envelope.payload.lines.map((line) => ({
        lineId: line.lineId,
        productId: line.productId,
        quantity: line.quantity,
        selections: line.selections.map((selection) => ({
          optionGroupId: selection.optionGroupId,
          optionIds: [...selection.optionIds],
        })),
        note: line.note,
      })),
      customerSubmission,
      pricePolicy: command.pricePolicy,
      priceDecision: command.priceDecision,
      unitSettlement: command.unitSettlement,
      validatedBenefits,
    });

    const channel = channelIntake.reconcile(envelope.submissionId, {
      runtimeIntakeId: command.intakeId,
      orderId: result.order.orderId,
      decision: 'accepted',
      reconciledAt: command.at,
    });
    appendFact(
      result.order,
      `channel:${envelope.submissionId}:accepted`,
      'channel.accepted',
      'channel',
      envelope.idempotencyKey,
      command.at,
      { submissionId: envelope.submissionId, source: envelope.source, operatorId: command.operatorId },
    );
    return { ...result, channel };
  };

  const rejectChannelSubmission = (command: RejectChannelSubmissionCommand): ChannelEnvelope => {
    const envelope = requireChannel(command.submissionId);
    if (envelope.status === 'accepted') throw new Error('RUNTIME_CHANNEL_ALREADY_ACCEPTED');
    const rejected = channelIntake.reconcile(envelope.submissionId, {
      runtimeIntakeId: command.runtimeIntakeId,
      decision: 'rejected',
      reason: command.reason,
      reconciledAt: command.at,
    });
    deps.facts.append({
      eventId: `channel:${envelope.submissionId}:rejected:${command.idempotencyKey}`,
      aggregateType: 'channel_submission',
      aggregateId: envelope.submissionId,
      factType: 'channel.rejected',
      businessDate: envelope.businessDate,
      occurredAt: command.at,
      recordedAt: command.at,
      schemaVersion: 1,
      source: { authority: 'channel', revision: envelope.idempotencyKey },
      payload: { source: envelope.source, reason: command.reason, operatorId: command.operatorId },
    });
    return rejected;
  };

  const productProjection = (productId: string): RuntimeCatalogProductProjection | null => {
      const product = deps.getProduct(productId);
      if (!product) return null;
      const policy = activePolicy();
      const base = policy.pricing.productBaseMinor[product.id];
      const basePriceMinor = Number.isSafeInteger(base) && (base ?? -1) >= 0 ? base! : null;
      const requirements = policy.availability.productRequirements[product.id];
      let availability: RuntimeCatalogProductProjection['availability'] = 'unbound';
      if (requirements) {
        const statuses = requirements.map((node) => policy.availability.nodes[node]);
        if (statuses.every((status) => status === 'available')) availability = 'available';
        else if (statuses.some((status) => status === undefined)) availability = 'unbound';
        else availability = 'blocked';
      }
      const visible = policy.visibility.operations[product.id] === true;
      const optionGroups = [...product.optionGroups]
        .filter((group) => group.enabled)
        .sort((left, right) => left.order - right.order)
        .map((group): RuntimeCatalogOptionGroupProjection => ({
          optionGroupId: group.id,
          name: group.name,
          required: group.required,
          min: group.min,
          max: group.max,
          options: [...group.options]
            .sort((left, right) => left.order - right.order)
            .map((option): RuntimeCatalogOptionProjection => {
              const delta = policy.pricing.modifierOptionMinor[modifierPriceKey(product.id, group.id, option.id)];
              return {
                optionId: option.id,
                name: option.name,
                enabled: option.enabled,
                priceDeltaMinor: Number.isSafeInteger(delta) ? delta! : null,
              };
            }),
        }));
      const requiresConfiguration = optionGroups.some((group) => group.required ? Math.max(1, group.min) > 0 : group.min > 0);
      return {
        productId: product.id,
        customerName: product.names.customer,
        operationsName: product.names.operations,
        currency: policy.currency,
        basePriceMinor,
        visible,
        availability,
        sellable: product.status === 'active' && visible && availability === 'available' && basePriceMinor !== null,
        requiresConfiguration,
        hasOptions: optionGroups.some((group) => group.options.length > 0),
        optionGroups,
      };
  };

  const listWorkspaceProductIds = (): readonly string[] => {
    const policy = activePolicy();
    return [...new Set([
      ...Object.keys(policy.pricing.productBaseMinor),
      ...Object.keys(policy.visibility.operations),
      ...Object.keys(policy.availability.productRequirements),
    ])].sort();
  };

  const baseSmtP01Workspace = createSmtP01WorkspaceAuthority({
    getActivePolicy: deps.getActivePolicy,
    getProduct: deps.getProduct,
    getProductProjection: productProjection,
    listProductIds: listWorkspaceProductIds,
    quote: (rawLines, policy, options) => quoteForExplicitComboCommit(
      rawLines as readonly OrderLineSnapshot[],
      policy,
      options?.confirmedComboMatches ?? [],
    ),
  });

  const smtP01Workspace = createSmtP01ActiveWorkAuthority(
    baseSmtP01Workspace,
    deps.getStoreId && deps.getBusinessDate && deps.staff
      ? {
          getStoreId: deps.getStoreId,
          getBusinessDate: deps.getBusinessDate,
          isActiveStaff: (actorId) => deps.staff!.staff(actorId)?.status === 'active',
          durability: createSmtP01WorkspaceDurability(deps.durability),
          appendFact: appendActiveWorkFact,
        }
      : undefined,
  );

  const runtime = {
    productProjection,
    previewDraft(inputs: readonly ProductSelectionInput[]): RuntimeDraftPreview {
      return previewDraft(inputs);
    },

    smtP01Workspace,

    placeOperatorWorkspaceOrder(
      command: OperatorWorkspaceOrderCommand,
      validatedBenefits: readonly ValidatedPricingBenefit[] = [],
    ): RuntimeOrderResult {
      const prepared = smtP01Workspace.prepareFinalization(command.workspaceId, command.expectedWorkspaceRevision);
      if (prepared.kind === 'rejected') throw new Error(prepared.code);
      return finalizeRuntimeOrder({
        intakeId: command.intakeId,
        orderId: command.orderId,
        idempotencyKey: command.idempotencyKey,
        businessDate: command.businessDate,
        source: command.source,
        createdAt: command.createdAt,
        operatorId: command.operatorId,
        deviceId: command.deviceId,
        customerId: command.customerId,
        serviceMode: command.serviceMode,
        lineServiceModes: command.lineServiceModes,
        lines: prepared.lines,
        unitSettlement: command.unitSettlement,
        confirmedComboMatches: prepared.confirmedComboMatches,
        validatedBenefits,
      });
    },

    async placeOperatorActiveWorkspaceOrder(
      command: ActiveOperatorWorkspaceOrderCommand,
      validatedBenefits: readonly ValidatedPricingBenefit[] = [],
    ): Promise<RuntimeOrderResult> {
      if (command.source !== command.surface) throw new Error('ACTIVE_WORK_SOURCE_SURFACE_MISMATCH');
      const currentBusinessDate = deps.getBusinessDate?.();
      if (currentBusinessDate && currentBusinessDate !== command.businessDate) throw new Error('ACTIVE_WORK_BUSINESS_DATE_MISMATCH');
      const handler = activeHandlerFor(command);
      const prepared = smtP01Workspace.prepareActiveFinalization(
        command.workspaceId,
        command.expectedWorkspaceRevision,
        handler,
        command.expectedCustodyRevision,
      );
      if (prepared.kind === 'rejected') throw new Error(prepared.code);
      if (command.serviceMode !== undefined && command.serviceMode !== prepared.serviceMode) {
        throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_MISMATCH');
      }
      if (command.lineServiceModes !== undefined && !sameLineServiceModes(command.lineServiceModes, prepared.lineServiceModes)) {
        throw new Error('ACTIVE_WORK_SERVICE_CONTEXT_MISMATCH');
      }
      const result = finalizeRuntimeOrder({
        intakeId: command.intakeId,
        orderId: command.orderId,
        idempotencyKey: command.idempotencyKey,
        businessDate: command.businessDate,
        source: command.source,
        createdAt: command.createdAt,
        operatorId: command.operatorId,
        deviceId: command.deviceId,
        customerId: command.customerId,
        serviceMode: prepared.serviceMode,
        lineServiceModes: prepared.lineServiceModes,
        lines: prepared.lines,
        unitSettlement: command.unitSettlement,
        confirmedComboMatches: prepared.confirmedComboMatches,
        validatedBenefits,
      });
      await smtP01Workspace.completeActiveFinalization({
        workspaceId: command.workspaceId,
        expectedWorkspaceRevision: command.expectedWorkspaceRevision,
        expectedCustodyRevision: command.expectedCustodyRevision,
        handler,
        orderId: result.order.orderId,
      });
      return result;
    },

    placeOperatorOrder(
      command: OperatorOrderCommand,
      validatedBenefits: readonly ValidatedPricingBenefit[] = [],
    ): RuntimeOrderResult {
      return finalizeRuntimeOrder({ ...command, validatedBenefits });
    },

    submitChannel(submission: ChannelSubmission): ChannelEnvelope {
      return channelIntake.submit(submission);
    },

    pendingChannelSubmissions(): readonly ChannelEnvelope[] {
      return channelIntake.list().filter((envelope) => envelope.status === 'submitted');
    },

    previewChannelSubmission(submissionId: string): RuntimeChannelPreview {
      const envelope = requireChannel(submissionId);
      try {
        const preview = previewDraft(envelope.payload.lines.map((line) => ({
          lineId: line.lineId,
          productId: line.productId,
          quantity: line.quantity,
          selections: line.selections.map((selection) => ({
            optionGroupId: selection.optionGroupId,
            optionIds: [...selection.optionIds],
          })),
          note: line.note,
        })));
        if (preview.quote?.kind !== 'quoted' || envelope.payload.customerDisplayedTotalMinor === undefined) {
          return { envelope, ...preview };
        }
        if (!envelope.payload.currency) {
          return { envelope, ...preview, errorCode: 'RUNTIME_CHANNEL_DISPLAYED_TOTAL_CURRENCY_REQUIRED' };
        }
        if (envelope.payload.currency !== preview.quote.evidence.pricing.currency) {
          return { envelope, ...preview, errorCode: 'RUNTIME_CHANNEL_CURRENCY_MISMATCH' };
        }
        return {
          envelope,
          ...preview,
          differenceMinor: preview.quote.evidence.pricing.totalMinor - envelope.payload.customerDisplayedTotalMinor,
        };
      } catch (error) {
        return { envelope, lines: [], quote: null, errorCode: errorCode(error) };
      }
    },

    acceptChannelOrder,
    rejectChannelSubmission,

    acceptSmmChannelOrder(
      command: SmmChannelOrderCommand,
      validatedBenefits: readonly ValidatedPricingBenefit[] = [],
    ): RuntimeOrderResult {
      if (command.submission.source !== 'smm' || command.submission.channel !== 'store') {
        throw new Error('RUNTIME_SMM_CHANNEL_REQUIRED');
      }
      const submitted = channelIntake.submit(command.submission);
      return acceptChannelOrder({
        submissionId: submitted.submissionId,
        intakeId: command.intakeId,
        orderId: command.orderId,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        deviceId: command.deviceId,
        customerId: command.customerId,
        serviceMode: command.serviceMode,
        lineServiceModes: command.lineServiceModes,
        at: command.at,
        unitSettlement: command.unitSettlement,
      }, validatedBenefits);
    },

    getOrder(orderId: string): CanonicalOrderSnapshot | null {
      return orderFinalizer.get(orderId);
    },

    paymentSnapshot(orderId: string): PaymentSnapshot {
      return deps.payment.snapshot(orderId);
    },

    fulfillmentSnapshot(orderId: string): FulfillmentSnapshot {
      return deps.fulfillment.snapshot(orderId);
    },

    allocatePaymentUnits(orderId: string, command: AllocateRuntimePaymentUnitsCommand): PaymentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.payment.allocateUnits(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
        payerId: command.payerId,
        unitIds: command.unitIds,
      });
      const snapshot = deps.payment.snapshot(orderId);
      appendFact(
        order,
        `payment:${orderId}:${command.idempotencyKey}`,
        'payment.units_allocated',
        'payment',
        String(result.version),
        command.at,
        { payerId: command.payerId, unitIds: command.unitIds },
      );
      return snapshot;
    },

    recordPayment(orderId: string, command: RecordRuntimePaymentCommand): PaymentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.payment.recordPayment(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
        method: command.method,
        amountMinor: command.amountMinor,
        ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
        reference: command.reference,
      });
      const snapshot = deps.payment.snapshot(orderId);
      appendFact(
        order,
        `payment:${orderId}:${command.idempotencyKey}`,
        'payment.received',
        'payment',
        String(result.version),
        command.at,
        {
          method: command.method,
          amountMinor: command.amountMinor,
          ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
          fullySettled: result.fullySettled,
        },
      );
      return snapshot;
    },

    recordCashTender(orderId: string, command: RecordRuntimeCashTenderCommand): RuntimeCashTenderResult {
      const order = requireOrder(orderId);
      const result = deps.payment.recordCashTender(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
        tenderedMinor: command.tenderedMinor,
        ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
      });
      const payment = deps.payment.snapshot(orderId);
      appendFact(
        order,
        `payment:${orderId}:${command.idempotencyKey}`,
        'payment.received',
        'payment',
        String(result.version),
        command.at,
        {
          method: 'CASH',
          tenderedMinor: command.tenderedMinor,
          changeMinor: result.changeMinor,
          ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
          fullySettled: result.fullySettled,
        },
      );
      return { payment, changeMinor: result.changeMinor };
    },

    recordRefund(orderId: string, command: RecordRuntimeRefundCommand): PaymentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.payment.recordRefund(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
        method: command.method,
        amountMinor: command.amountMinor,
        ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
        ...(command.reference !== undefined ? { reference: command.reference } : {}),
        reason: command.reason,
      });
      const snapshot = deps.payment.snapshot(orderId);
      appendFactForBusinessDate(
        order,
        command.businessDate,
        `payment:${orderId}:${command.idempotencyKey}`,
        'payment.refunded',
        'payment',
        String(result.version),
        command.at,
        {
          method: command.method,
          amountMinor: command.amountMinor,
          ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
          ...(command.reference !== undefined ? { reference: command.reference } : {}),
          reason: command.reason,
          originalOrderBusinessDate: order.businessDate,
          fullySettled: result.fullySettled,
        },
      );
      return snapshot;
    },

    recordCorrection(orderId: string, command: RecordRuntimeCorrectionCommand): PaymentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.payment.recordCorrection(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
        direction: command.direction,
        method: command.method,
        amountMinor: command.amountMinor,
        ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
        ...(command.reference !== undefined ? { reference: command.reference } : {}),
        reason: command.reason,
      });
      const snapshot = deps.payment.snapshot(orderId);
      appendFactForBusinessDate(
        order,
        command.businessDate,
        `payment:${orderId}:${command.idempotencyKey}`,
        'payment.corrected',
        'payment',
        String(result.version),
        command.at,
        {
          direction: command.direction,
          method: command.method,
          amountMinor: command.amountMinor,
          ...(command.payerId !== undefined ? { payerId: command.payerId } : {}),
          ...(command.reference !== undefined ? { reference: command.reference } : {}),
          reason: command.reason,
          originalOrderBusinessDate: order.businessDate,
          fullySettled: result.fullySettled,
        },
      );
      return snapshot;
    },

    authorizeProduction(orderId: string, command: RuntimeMutationCommand): FulfillmentSnapshot {
      const order = requireOrder(orderId);
      const payment = deps.payment.snapshot(orderId);
      const result = deps.fulfillment.authorizeProduction(orderId, {
        writer: 'smt-local',
        expectedVersion: command.expectedVersion,
        idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId,
        at: command.at,
      }, payment);
      appendFact(order, `fulfillment:${orderId}:${command.idempotencyKey}`, 'fulfillment.production_authorized', 'fulfillment', String(result.version), command.at, { state: result.state });
      return result;
    },

    markReady(orderId: string, command: RuntimeMutationCommand): FulfillmentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.fulfillment.markReady(orderId, {
        writer: 'smt-local', expectedVersion: command.expectedVersion, idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId, at: command.at,
      });
      appendFact(order, `fulfillment:${orderId}:${command.idempotencyKey}`, 'fulfillment.ready', 'fulfillment', String(result.version), command.at, { state: result.state });
      return result;
    },

    markCompleted(orderId: string, command: RuntimeMutationCommand): FulfillmentSnapshot {
      const order = requireOrder(orderId);
      const result = deps.fulfillment.markCompleted(orderId, {
        writer: 'smt-local', expectedVersion: command.expectedVersion, idempotencyKey: command.idempotencyKey,
        operatorId: command.operatorId, at: command.at,
      });
      appendFact(order, `fulfillment:${orderId}:${command.idempotencyKey}`, 'fulfillment.completed', 'fulfillment', String(result.version), command.at, { state: result.state });
      return result;
    },

    async queueOrderPrint(command: QueueOrderPrintCommand): Promise<{ readonly job: PrintJobSnapshot; readonly outbox: OutboxRecord }> {
      const order = requireOrder(command.orderId);
      const job = deps.print.createIntent({
        intentId: command.intentId,
        orderId: order.orderId,
        orderRevision: order.revision,
        documentKind: command.documentKind,
        templateVersion: command.templateVersion,
        payloadVersion: command.payloadVersion,
        payload: order,
        targets: command.targets,
        requestedBy: command.requestedBy,
      });
      const outbox = await deps.durability.enqueue({
        id: `print:${job.intentId}`,
        kind: 'print.intent',
        payload: { intentId: job.intentId, dedupeKey: job.dedupeKey, orderId: order.orderId, orderRevision: order.revision },
        createdAt: command.at,
        status: 'pending',
      });
      appendFact(order, `print:${job.intentId}:queued`, 'print.queued', 'printing', job.dedupeKey, command.at, {
        intentId: job.intentId,
        documentKind: job.documentKind,
        targets: job.targets.map(({ role, copies }) => ({ role, copies })),
      });
      return { job, outbox };
    },

    facts(): readonly OperationalFact[] {
      return deps.facts.all();
    },
  };

  const smtOrders = createSmtOrdersSurface({
    listOrders: () => orderFinalizer.list(),
    paymentSnapshot: (orderId) => deps.payment.snapshot(orderId),
    fulfillmentSnapshot: (orderId) => deps.fulfillment.snapshot(orderId),
    listPrintJobs: (orderId) => deps.print.listByOrder(orderId),
    projectFulfillment: projectSmtFulfillment,
    requiredFlowCanCheckout: (snapshot) => summarizeRequiredFlow(snapshot.requiredTasks).canCheckout,
    ...(deps.staff ? {
      authorizeStaff: ({ actorId, permission, at }) => {
        deps.staff!.authorize({ actorId, permission, at });
      },
    } : {}),
    authorizeProduction: (orderId, command) => runtime.authorizeProduction(orderId, command),
    markReady: (orderId, command) => runtime.markReady(orderId, command),
    markCompleted: (orderId, command) => runtime.markCompleted(orderId, command),
    recordRefund: (orderId, command) => runtime.recordRefund(orderId, command),
    recordCorrection: (orderId, command) => runtime.recordCorrection(orderId, command),
  });

  const smtDineIn = createSmtDineInSurface({
    getTableAuthority: () => deps.table ?? null,
    getOrder: (orderId) => orderFinalizer.get(orderId),
    paymentSnapshot: (orderId) => deps.payment.snapshot(orderId),
  });

  const smtAvailability = createSmtAvailabilitySurface({
    getAvailabilityAuthority: () => deps.availability ?? null,
    getPolicy: () => deps.getActivePolicy(),
    ...(deps.staff ? {
      authorizeStaff: ({ actorId, permission, at }) => {
        deps.staff!.authorize({ actorId, permission, at });
      },
    } : {}),
  });

  const smtBusinessDay = createSmtBusinessDaySurface({
    getCoordinator: () => deps.businessDay ?? null,
    ...(deps.staff ? {
      authorizeStaff: ({ actorId, permission, at }) => {
        deps.staff!.authorize({ actorId, permission, at });
      },
    } : {}),
  });

  const smtReporting = createSmtReportingSurface({
    getFacts: () => deps.facts.all(),
    getBusinessDay: () => deps.businessDay?.projection() ?? null,
    getTable: () => deps.table?.projection() ?? null,
    ...(deps.staff ? {
      authorizeStaff: ({ actorId, permission, at }) => {
        deps.staff!.authorize({ actorId, permission, at });
      },
    } : {}),
  });

  const smtPrinting = createSmtPrintingSurface({
    print: deps.print,
  });

  return { ...runtime, smtOrders, smtDineIn, smtAvailability, smtBusinessDay, smtReporting, smtPrinting };
}
